import { useCallback, useSyncExternalStore } from 'react';
import { useMeQuery } from '@platform/state/api';
import type { CustomizationScope } from './CustomizationScope';
import type { ICustomizationScopeState } from './ICustomizationScopeState';
import { resolveScope } from './resolveScope';

const STORAGE_KEY = 'po.custScope.v1';
const GLOBAL_ROLES = ['Platform admin', 'Customizer'];
const listeners = new Set<() => void>();

function readAll(): Record<string, CustomizationScope> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, CustomizationScope>) : {};
  } catch {
    return {};
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => { listeners.delete(listener); window.removeEventListener('storage', listener); };
}

/**
 * Рівень налаштування користувача (прототип v4 custScope + canCustWs/canCustGlobal): робочий простір — можливість customize у просторі
 * (власник, адміністратор), глобально — глобальна роль «Адміністратор платформи» чи «Налаштувальник»; вибір пам'ятається для користувача.
 */
export function useCustomizationScope(ws: string): ICustomizationScopeState {
  const { data: me } = useMeQuery();
  const userId = me?.id;
  const stored = useSyncExternalStore(subscribe, () => (userId ? readAll()[userId] ?? null : null), () => null);
  const canWs = !!me?.workspaces.find((w) => w.id === ws)?.myCapabilities.includes('customize');
  const canGlobal = GLOBAL_ROLES.includes(me?.globalRole ?? '');
  const setScope = useCallback((scope: CustomizationScope) => {
    if (!userId) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [userId]: scope })); } catch { /* приватний режим */ }
    listeners.forEach((l) => l());
  }, [userId]);
  return { scope: resolveScope(stored, canWs, canGlobal), setScope, canWs, canGlobal, noRights: !canWs && !canGlobal };
}
